import { useEffect, useRef, useState } from 'react';
import logoIcon from '@svg/header_logo.svg';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@hooks/context/useUserContext';
import { getFirstLetter, getLoyaltyLevelString } from '@utils/index';
import styles from './styles.module.scss';
import NavLink from '@components/common/navlink';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import { routes } from '@const/routes';
import useClickOutside from '@hooks/helpers/useClickOutside';
import { useLocales } from '@hooks/helpers/useLocales';
import { EUserStatus, EUserType } from '@xeppt/xeppt-sdk/types';
import { useNotificationsContext } from '@hooks/context/useNotificationsContext';
import { useKycModals } from '@hooks/modals/useKycModals';
import NotificationsSidebar from '@sections/notifications';
import useResponsive from '@hooks/helpers/useResponsive';
import { useIsFirstRender } from '@mantine/hooks';
import {
    dropdownProfileDesktopAnimation,
    dropdownProfileResponsiveAnimation
} from '@const/animation';
import { motion } from 'framer-motion';
import { apiUserService } from '@api';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';

const cx = classNames.bind(styles);

const Header = () => {
    const profileRef = useRef(null);
    const mobileRef = useRef(null);
    const location = useLocation();
    const [isOpenMobileMenu, setStateIsOpenMobileMenu] = useState(false);
    const [isOpenProfile, setIsOpenProfile] = useState(false);
    const [isOpenNotifications, setIsOpenNotifications] = useState(false);
    const { user, handleLogout, loyalty, userStatus } = useUserContext();
    const { unreadNotifications, refetchNotifications } = useNotificationsContext();
    const { t, i18n } = useTranslation('header');
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const { handleOpenKycFirstModal } = useKycModals();
    const { isMobile } = useResponsive();
    const isFirstRender = useIsFirstRender();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();

    const handleChangeLanguage = () => {
        const lang = i18n.language === 'en' ? 'fr' : 'en';
        i18n.changeLanguage(lang);
        localStorage.setItem('language', lang);
        if (!!user) {
            apiUserService
                .updateLang(lang)
                .then(() => requestSuccessLocale('change_language'))
                .catch(requestErrorLocale);
        }
    };

    const toggleUserProfile = () => {
        setIsOpenProfile((state) => !state);
    };

    const toggleMobileMenu = () => {
        setStateIsOpenMobileMenu((state) => !state);
    };

    const toggleNotifications = () => {
        setIsOpenNotifications((state) => !state);
    };

    useEffect(() => {
        setIsOpenProfile(false);
        setStateIsOpenMobileMenu(false);
        setIsOpenNotifications(false);
    }, [location]);

    useClickOutside({
        ref: mobileRef,
        callback: () => {
            setStateIsOpenMobileMenu(false);
        },
        event: 'mousedown'
    });

    useClickOutside({
        ref: profileRef,
        callback: () => {
            setIsOpenProfile(false);
        },
        event: 'mousedown'
    });

    useEffect(() => {
        if (isOpenNotifications) {
            window.scrollTo(0, 0);
            document.body.style.overflow = 'hidden';
            if (!isFirstRender) {
                refetchNotifications();
            }
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpenNotifications]);

    useEffect(() => {
        if ((isOpenMobileMenu || isOpenProfile) && isMobile) {
            window.scrollTo(0, 0);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpenMobileMenu, isOpenProfile, isMobile]);

    return (
        <>
            <header className={styles.wrapper}>
                <div className={styles.container}>
                    <div className={styles.left}>
                        {!!user && (
                            <div className={styles.mobile_menu} ref={mobileRef}>
                                <button
                                    className={cx([styles.burger, { isOpen: isOpenMobileMenu }])}
                                    onClick={toggleMobileMenu}>
                                    <div />
                                    <div />
                                    <div />
                                </button>
                                <div
                                    className={cx([styles.dropdown, { isOpen: isOpenMobileMenu }])}>
                                    <NavLink className={styles.action} href={routes.dashboard}>
                                        <Typography variant="body3">{t('home')}</Typography>
                                    </NavLink>
                                    <NavLink
                                        routes={[
                                            routes.send_and_request_autodeposit,
                                            routes.send_and_request_contacts,
                                            routes.send_and_request_send,
                                            routes.send_and_request_request,
                                            routes.send_and_request_schedule
                                        ]}
                                        className={styles.action}
                                        href={routes.send_and_request}>
                                        <Typography variant="body3">
                                            {t('send_and_request')}
                                        </Typography>
                                    </NavLink>
                                    <NavLink
                                        routes={[
                                            routes.pay_bills_pay,
                                            routes.pay_bills_payees,
                                            routes.pay_bills_schedule
                                        ]}
                                        className={styles.action}
                                        href={routes.pay_bills}>
                                        <Typography variant="body3">{t('pay_bills')}</Typography>
                                    </NavLink>
                                    {user.type === EUserType.BUSINESS && (
                                        <NavLink
                                            routes={[routes.gift_cards, routes.gift_cards_create]}
                                            className={styles.action}
                                            href={routes.gift_cards}>
                                            <Typography variant="body3">
                                                {t('gift_cards')}
                                            </Typography>
                                        </NavLink>
                                    )}
                                </div>
                            </div>
                        )}
                        <Link to={!user ? routes.signin : routes.dashboard} className={styles.logo}>
                            <img src={logoIcon} alt="logo" />
                        </Link>
                    </div>
                    {!!user && (
                        <nav className={styles.navigation}>
                            <ul>
                                <li>
                                    <NavLink href={routes.dashboard}>{t('home')}</NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        routes={[
                                            routes.send_and_request_autodeposit,
                                            routes.send_and_request_contacts,
                                            routes.send_and_request_send,
                                            routes.send_and_request_request,
                                            routes.send_and_request_schedule
                                        ]}
                                        href={routes.send_and_request}>
                                        {t('send_and_request')}
                                    </NavLink>
                                </li>
                                <li>
                                    <NavLink
                                        routes={[
                                            routes.pay_bills_pay,
                                            routes.pay_bills_payees,
                                            routes.pay_bills_schedule
                                        ]}
                                        href={routes.pay_bills}>
                                        {t('pay_bills')}
                                    </NavLink>
                                </li>
                                {user.type === EUserType.BUSINESS && (
                                    <li>
                                        <NavLink
                                            routes={[routes.gift_cards, routes.gift_cards_create]}
                                            href={routes.gift_cards}>
                                            {t('gift_cards')}
                                        </NavLink>
                                    </li>
                                )}
                            </ul>
                        </nav>
                    )}
                    <div className={styles.right}>
                        <Button className={styles.language} onClick={handleChangeLanguage}>
                            <Typography
                                className={i18n.language === 'en' ? styles.isActive : ''}
                                variant="body3">
                                EN
                            </Typography>
                            <span>|</span>
                            <Typography
                                className={i18n.language === 'fr' ? styles.isActive : ''}
                                variant="body3">
                                FR
                            </Typography>
                        </Button>
                        {!!user && (
                            <>
                                <div className={styles.notifications_wrapper}>
                                    <Button
                                        variant="icon"
                                        className={styles.notification}
                                        notify={unreadNotifications > 0}
                                        notifyCount={unreadNotifications}
                                        onClick={toggleNotifications}>
                                        <Icon name="notification" />
                                    </Button>
                                </div>
                                <div className={styles.profile_wrapper} ref={profileRef}>
                                    <div
                                        className={styles.avatar_wrapper}
                                        onClick={toggleUserProfile}>
                                        {user?.profile?.avatar ? (
                                            <img
                                                className={styles.avatar}
                                                src={user?.profile?.avatar}
                                                alt={`${user?.profile?.first_name} avatar`}
                                            />
                                        ) : (
                                            <div className={styles.logo_symbols}>
                                                {getFirstLetter(user?.profile?.first_name)}
                                                {getFirstLetter(user?.profile?.last_name)}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <button
                                            className={styles.profile_button}
                                            onClick={toggleUserProfile}>
                                            <Typography variant="body4">
                                                {user?.profile?.first_name}{' '}
                                                {user?.profile?.last_name}
                                            </Typography>
                                            <Icon name="nav_down" />
                                        </button>
                                        {isOpenProfile && (
                                            <motion.div
                                                {...(isMobile
                                                    ? dropdownProfileResponsiveAnimation
                                                    : dropdownProfileDesktopAnimation)}
                                                className={cx([
                                                    styles.profile_dropdown,
                                                    { isOpen: isOpenProfile }
                                                ])}>
                                                <div className={styles.profile_dropdown_content}>
                                                    <div
                                                        className={styles.avatar_wrapper}
                                                        onClick={toggleUserProfile}>
                                                        {user?.profile?.avatar ? (
                                                            <img
                                                                className={styles.avatar}
                                                                src={user?.profile?.avatar}
                                                                alt={`${user?.profile?.first_name} avatar`}
                                                            />
                                                        ) : (
                                                            <div className={styles.logo_symbols}>
                                                                {getFirstLetter(
                                                                    user?.profile?.first_name
                                                                )}
                                                                {getFirstLetter(
                                                                    user?.profile?.last_name
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Typography
                                                        className={styles.name}
                                                        variant="h5"
                                                        weight="semibold">
                                                        {user?.profile?.first_name}{' '}
                                                        {user?.profile?.last_name}
                                                    </Typography>
                                                    <Typography
                                                        className={styles.email}
                                                        variant="body4">
                                                        {user?.email}
                                                    </Typography>
                                                    <div className={styles.loyalty_level}>
                                                        <Icon name="award" />
                                                        <Typography variant="body4">
                                                            Profile level:{' '}
                                                            <b>
                                                                {getLoyaltyLevelString(
                                                                    loyalty?.level
                                                                )}
                                                            </b>
                                                        </Typography>
                                                    </div>
                                                    <div className={styles.divider} />
                                                    <Link
                                                        className={styles.action}
                                                        to={routes.settings}>
                                                        <Icon name="settings" />
                                                        <Typography variant="body3">
                                                            {t('settings')}
                                                        </Typography>
                                                    </Link>
                                                    <Link
                                                        className={styles.action}
                                                        to={routes.cashback_history}>
                                                        <Icon name="gift" />
                                                        <Typography variant="body3">
                                                            My cashback
                                                        </Typography>
                                                    </Link>
                                                    <Link
                                                        className={styles.action}
                                                        to={routes.my_loyalty_points}>
                                                        <Icon name="coins" />
                                                        <Typography variant="body3">
                                                            My points
                                                        </Typography>
                                                    </Link>
                                                    <Link
                                                        className={styles.action}
                                                        to={routes.help_center}>
                                                        <Icon name="question" />
                                                        <Typography variant="body3">
                                                            {t('help_center')}
                                                        </Typography>
                                                    </Link>
                                                    <div
                                                        className={`${styles.divider} ${styles.mobile_divider}`}
                                                    />
                                                </div>
                                                <Link
                                                    to={routes.signin}
                                                    className={cx([
                                                        styles.action,
                                                        styles.logout_link
                                                    ])}
                                                    onClick={() => handleLogout()}>
                                                    <Icon name="logout" />
                                                    <Typography
                                                        variant="body3"
                                                        className={styles.logout}>
                                                        {t('logout')}
                                                    </Typography>
                                                </Link>
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </header>
            {(userStatus === EUserStatus.UNVERIFIED || userStatus === EUserStatus.PENDING) && (
                <div className={styles.kyc_wrapper}>
                    <div className={styles.container}>
                        <Typography variant="body4">
                            <Icon name="kyc" />
                            {t(
                                userStatus === EUserStatus.PENDING
                                    ? 'verification_pending'
                                    : 'verification'
                            )}
                        </Typography>
                        {userStatus === EUserStatus.UNVERIFIED && (
                            <Button
                                onClick={handleOpenKycFirstModal}
                                size="small"
                                variant="primary">
                                {submitLocale('get_verified')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {userStatus === EUserStatus.SUSPENDED && (
                <div className={styles.suspended_wrapper}>
                    <div className={styles.container}>
                        <Typography variant="body4">
                            <Icon name="warning" />
                            Your account is temporarily suspended. Please contact support to regain
                            access to all services.
                        </Typography>
                        <Button
                            onClick={handleOpenSubmitRequestModal}
                            size="small"
                            variant="primary">
                            {submitLocale('contact_support')}
                        </Button>
                    </div>
                </div>
            )}
            <NotificationsSidebar
                isOpen={isOpenNotifications}
                onClose={() => setIsOpenNotifications(false)}
            />
        </>
    );
};

export default Header;
